import React from 'react';
import {
  Globe,
  Mic,
  Users,
  Headphones,
  Monitor,
  Languages,
  Zap,
  Shield,
  Cloud,
  Settings,
  Volume2,
  Eye,
  Heart,
  Accessibility,
  Radio,
  MessageSquare, ArrowLeft
} from 'lucide-react';
import {useNavigate} from "react-router-dom";

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, features }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white mr-4 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start text-sm text-gray-700">
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Services: React.FC = () => {
  const navigate = useNavigate();

  const coreServices = [
    {
      icon: <Mic className="h-6 w-6" />,
      title: "Live Sermon Translation",
      description: "Transform your sermons into a multilingual experience with our AI-powered real-time translation system.",
      features: [
        "Real-time sermon translation into 100+ languages",
        "AI-powered sermon translator with 95%+ accuracy",
        "Live audio translation platform for seamless delivery",
        "Automatic sermon translator that works instantly",
        "Speech-to-text sermon converter with natural language processing"
      ]
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: "AI Captioning & Subtitles",
      description: "Provide inclusive worship experiences with intelligent captioning and subtitle generation.",
      features: [
        "AI captioning for religious content and services",
        "Live captioning for religious events and gatherings",
        "Instant subtitle for sermons and worship sessions",
        "Live subtitle generator for sermons with real-time display",
        "Sermon subtitle translation software for global reach"
      ]
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Multilingual Worship Experience",
      description: "Create truly inclusive worship services that welcome congregants from every nation.",
      features: [
        "Multilingual worship translation for diverse congregations",
        "Bilingual church service software for seamless integration",
        "Cross-language sermon delivery to break down barriers",
        "Multilingual sermon translation with cultural sensitivity",
        "Translation tool for international churches and missions"
      ]
    },
    {
      icon: <Accessibility className="h-6 w-6" />,
      title: "Accessibility Solutions",
      description: "Ensure every member of your congregation can fully participate in worship and fellowship.",
      features: [
        "Accessibility tool for churches serving diverse communities",
        "Sermon translation for the deaf and hard of hearing",
        "Inclusive church translation tech for all abilities",
        "Real-time religious transcription for accessibility compliance",
        "Audio translation for sermons with clear voice synthesis"
      ]
    },
    {
      icon: <Monitor className="h-6 w-6" />,
      title: "Church Broadcasting Solutions",
      description: "Enhance your livestreams and broadcasts with professional multilingual capabilities.",
      features: [
        "Church broadcast with multilingual support",
        "Church livestream subtitle integration",
        "Sermon streaming translation support for online services",
        "Live service translation solution for hybrid worship",
        "Translation-enabled church streaming for global reach"
      ]
    },
    {
      icon: <Cloud className="h-6 w-6" />,
      title: "Cloud-Based Translation Platform",
      description: "Leverage the power of cloud computing for scalable, reliable translation services.",
      features: [
        "Cloud-based sermon translator with enterprise reliability",
        "Scalable sermon translation system that grows with your ministry",
        "Real-time ministry translation tools in the cloud",
        "Church live translation software with 99.9% uptime",
        "Faith-based live translation software built for churches"
      ]
    }
  ];

  const specializedServices = [
    {
      icon: <Headphones className="h-6 w-6" />,
      title: "Audio Interpretation Services",
      description: "Professional-grade audio interpretation for church services and religious events.",
      features: [
        "Church audio interpretation software",
        "Language interpretation for churches",
        "AI church interpretation service",
        "Religious speech AI tool",
        "Live spiritual content translation"
      ]
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Church Presentation Tools",
      description: "Integrated tools for creating multilingual presentations and worship materials.",
      features: [
        "Multilingual church presentation tools",
        "Church speech translator software",
        "Bilingual sermon translator integration",
        "Church subtitle automation",
        "Subtitle generation for churches"
      ]
    },
    {
      icon: <Radio className="h-6 w-6" />,
      title: "Simultaneous Translation System",
      description: "Professional simultaneous translation for large gatherings and conferences.",
      features: [
        "Simultaneous translation system for worship",
        "Real-time church translation",
        "Live translation into multiple languages",
        "Religious event subtitle service",
        "Foreign language church translation"
      ]
    }
  ];

  const keyBenefits = [
    {
      icon: <Globe className="h-8 w-8 text-indigo-600" />,
      title: "Global Ministry Reach",
      description: "Expand your ministry to reach congregations worldwide with seamless translation technology."
    },
    {
      icon: <Zap className="h-8 w-8 text-indigo-600" />,
      title: "Real-Time Processing",
      description: "Experience instant translation with minimal latency for natural, flowing worship services."
    },
    {
      icon: <Shield className="h-8 w-8 text-indigo-600" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security ensures your sermons and services remain private and protected."
    },
    {
      icon: <Heart className="h-8 w-8 text-indigo-600" />,
      title: "Ministry-Focused",
      description: "Built specifically for churches with features designed for religious content and worship."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <button
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 flex items-center text-white hover:text-indigo-100 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Home
      </button>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Complete Translation Services for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-400 block">
                Modern Churches
              </span>
            </h1>
            <p className="text-xl text-indigo-100 mb-8 leading-relaxed">
              Discover our comprehensive suite of AI-powered translation and accessibility tools designed specifically for churches, ministries, and religious organizations worldwide.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                Real-Time Religious Translation Software
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                AI Church Translation Software
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                Multilingual Church Translation System
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core Translation Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our flagship services provide everything your church needs for comprehensive multilingual ministry
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((service, index) => (
              <ServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose SAI
            </h2>
            <p className="text-xl text-gray-600">
              Built specifically for religious organizations with ministry-focused features
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyBenefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Solutions
            </h2>
            <p className="text-xl text-gray-600">
              Advanced features for specific ministry needs and use cases
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {specializedServices.map((service, index) => (
              <ServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Technology Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Technology Features
            </h2>
            <p className="text-xl text-gray-600">
              Cutting-edge AI and cloud technology powering your ministry
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
              <Volume2 className="h-8 w-8 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Processing</h3>
              <p className="text-gray-600 mb-4">
                Experience real-time worship translation and real-time religious translation software with minimal latency.
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Real-time captioning software for churches</li>
                <li>• Live sermon translation tool</li>
                <li>• Religious speech translator</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <Eye className="h-8 w-8 text-green-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Accuracy</h3>
              <p className="text-gray-600 mb-4">
                Leverage AI-powered religious event subtitles and AI translation for churches with industry-leading accuracy.
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• AI-powered sermon translator</li>
                <li>• Religious service translation</li>
                <li>• Sermon translation service</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
              <Languages className="h-8 w-8 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Language Support</h3>
              <p className="text-gray-600 mb-4">
                Support for 100+ languages with multilingual church translation system capabilities.
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Translate church service live</li>
                <li>• Sermon translator tool</li>
                <li>• Religious event subtitle service</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Ministry?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join thousands of churches worldwide using SAI to break down language barriers and create inclusive worship experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold hover:bg-indigo-50 transition-colors">
              Start Free Trial
            </button>
            <button className="bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;